// Copyright (c) Ping. All rights reserved.

import React from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { notesStrings } from './strings';
import type { NotesSelection } from './types';

export interface NotesSelectionToolbarProps {
  readonly selection: NotesSelection;
  readonly onBulkDelete: () => void;
  readonly onBulkMove: () => void;
  readonly onClear: () => void;
}

export const NotesSelectionToolbar = React.memo<NotesSelectionToolbarProps>((props) => {
  const { selection, onBulkDelete, onBulkMove, onClear } = props;
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');

  if (selection.mode !== 'active') {
    return null;
  }

  const count = selection.selectedIds.size;
  const disabled = count === 0;

  return (
    <View
      style={[
        styles.bar,
        {
          backgroundColor: theme.colors.surface.secondary,
          borderTopColor: theme.colors.border.secondary,
        },
      ]}
      accessibilityLabel={notesStrings.selection.countLabel(count)}
    >
      <Text style={[styles.count, { color: theme.colors.text.primary }]}>
        {notesStrings.selection.countLabel(count)}
      </Text>
      <View style={styles.actions}>
        <ToolbarButton
          label={notesStrings.selection.bulkMove}
          color={theme.colors.text.primary}
          disabled={disabled}
          onPress={onBulkMove}
        />
        <ToolbarButton
          label={notesStrings.selection.bulkDelete}
          color={theme.colors.accent.rose}
          disabled={disabled}
          onPress={onBulkDelete}
        />
        <ToolbarButton
          label={notesStrings.selection.exit}
          color={theme.colors.text.secondary}
          disabled={false}
          onPress={onClear}
        />
      </View>
    </View>
  );
});

NotesSelectionToolbar.displayName = 'NotesSelectionToolbar';

interface ToolbarButtonProps {
  readonly label: string;
  readonly color: string;
  readonly disabled: boolean;
  readonly onPress: () => void;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ label, color, disabled, onPress }) => (
  <Pressable
    onPress={() => {
      if (!disabled) {
        onPress();
      }
    }}
    accessibilityRole="button"
    accessibilityLabel={label}
    accessibilityState={{ disabled }}
    disabled={disabled}
    style={({ pressed }) => [styles.button, pressed && !disabled && { opacity: 0.7 }]}
  >
    <Text
      style={[
        styles.buttonText,
        {
          color,
          opacity: disabled ? 0.4 : 1,
        },
      ]}
    >
      {label}
    </Text>
  </Pressable>
);

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'column',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  count: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
  } satisfies TextStyle,
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  } satisfies ViewStyle,
  button: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginRight: 8,
  } satisfies ViewStyle,
  buttonText: {
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
});
